// File type detection and icon utilities

export const getFileType = (fileName, mimeType) => {
  const extension = fileName.split('.').pop().toLowerCase()
  
  // Document types
  if (['pdf'].includes(extension) || mimeType?.includes('pdf')) {
    return { type: 'pdf', icon: 'document-text', color: 'red' }
  }
  
  if (['doc', 'docx'].includes(extension) || mimeType?.includes('word')) {
    return { type: 'document', icon: 'document-text', color: 'blue' }
  }
  
  if (['xls', 'xlsx', 'csv'].includes(extension) || mimeType?.includes('spreadsheet') || mimeType?.includes('excel')) {
    return { type: 'spreadsheet', icon: 'table', color: 'green' }
  }
  
  if (['ppt', 'pptx'].includes(extension) || mimeType?.includes('presentation')) {
    return { type: 'presentation', icon: 'presentation-chart-bar', color: 'orange' }
  }
  
  // Image types
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(extension) || mimeType?.includes('image')) {
    return { type: 'image', icon: 'photograph', color: 'purple' }
  }
  
  // Video types
  if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(extension) || mimeType?.includes('video')) {
    return { type: 'video', icon: 'film', color: 'indigo' }
  }
  
  // Audio types
  if (['mp3', 'wav', 'flac', 'aac', 'ogg', 'm4a'].includes(extension) || mimeType?.includes('audio')) {
    return { type: 'audio', icon: 'music-note', color: 'pink' }
  }
  
  // Archive types
  if (['zip', 'rar', '7z', 'tar', 'gz', 'bz2'].includes(extension)) {
    return { type: 'archive', icon: 'archive', color: 'yellow' }
  }
  
  // Code types
  if (['js', 'ts', 'jsx', 'tsx', 'vue', 'html', 'css', 'scss', 'json', 'xml', 'php', 'py', 'java', 'cpp', 'c', 'cs', 'rb', 'go', 'rs'].includes(extension)) {
    return { type: 'code', icon: 'code', color: 'gray' }
  }
  
  // Text types
  if (['txt', 'md', 'readme'].includes(extension) || mimeType?.includes('text')) {
    return { type: 'text', icon: 'document-text', color: 'gray' }
  }
  
  // Default
  return { type: 'file', icon: 'document', color: 'gray' }
}

export const getFileIcon = (fileName, mimeType) => {
  const fileInfo = getFileType(fileName, mimeType)
  return fileInfo.icon
}

export const getFileColor = (fileName, mimeType) => {
  const fileInfo = getFileType(fileName, mimeType)
  const colors = {
    red: 'text-red-600 bg-red-100',
    blue: 'text-blue-600 bg-blue-100',
    green: 'text-green-600 bg-green-100',
    orange: 'text-orange-600 bg-orange-100',
    purple: 'text-purple-600 bg-purple-100',
    indigo: 'text-indigo-600 bg-indigo-100',
    pink: 'text-pink-600 bg-pink-100',
    yellow: 'text-yellow-600 bg-yellow-100',
    gray: 'text-gray-600 bg-gray-100'
  }
  return colors[fileInfo.color] || colors.gray
}

// SVG Icons for different file types
export const fileIcons = {
  'document-text': `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>`,
  'table': `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M3 14h18m-9-4v8m-7 0V4a1 1 0 011-1h16a1 1 0 011 1v16a1 1 0 01-1 1H4a1 1 0 01-1-1z"></path>`,
  'presentation-chart-bar': `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>`,
  'photograph': `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>`,
  'film': `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 4v16l13-8L7 4z"></path>`,
  'music-note': `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"></path>`,
  'archive': `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"></path>`,
  'code': `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"></path>`,
  'document': `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>`
}

export default {
  getFileType,
  getFileIcon,
  getFileColor,
  fileIcons
}
